"use client"
import { Track } from "@opensea/analytics"
import {
  type FragmentOf,
  graphql,
  type ResultOf,
  readFragment,
} from "@opensea/graphql"
import { HandoffButton, type ItemView } from "@opensea/navigation"
import { Link } from "@opensea/next-ui-kit"
import {
  FlexColumn,
  LiveAnimation,
  NumberDisplay,
  QuantityBadge,
  SpaceBetween,
  TextBody,
  TextOverflowTooltip,
  Tooltip,
} from "@opensea/ui-kit"
import { useSelectItemMouseShortcuts } from "@opensea/ui-kit/hooks"
import { isAddressEqual } from "@opensea/utils"
import { useCreateOfferFlow } from "@opensea/wallet"
import { differenceInMinutes } from "date-fns"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useContextSelector } from "use-context-selector"
import { LiveTimestamp } from "@/components/activity-feed/LiveTimestamp"
import { CollectionSettingsContext } from "@/components/collection/CollectionSettingsProvider"
import { TokenPrice } from "@/components/price/TokenPrice"
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment"
import {
  EnforcementBadge,
  EnforcementBadgeFragment,
} from "@/components/trustandsafety/EnforcementBadge"
import { CRYPTO_PUNKS_ADDRESS } from "@/constants/constants"
import { useStatusQueryParam } from "@/hooks/query-param/use-status-query-param"
import {
  bestItemOfferFragment,
  getItemBestOffer,
} from "@/lib/fragments/best-order"
import { itemCardActionVariants } from "../../card/constants"
import { useItemCardTokenPriceMarketplaceProps } from "../../card/hooks"
import {
  ItemCard,
  ItemCardActionButton,
  ItemCardActionSection,
  ItemCardActions,
  ItemCardContent,
  ItemCardFooter,
  ItemCardFooterDescription,
  ItemCardFooterSection,
  ItemCardMedia,
  ItemCardName,
  ItemCardPriceLabel,
  ItemCardRarityBadge,
  ItemCardSelectItemBadge,
  ItemCardSelectItemWithQuantityBadge,
  ItemCardTooltipButtonWrapper,
} from "../../card/ItemCard"
import { ItemCardMediaFragment } from "../../card/ItemCardMedia/ItemCardMedia.fragment"
import { ItemCardNameFragment } from "../../card/ItemCardName"
import type { ItemCardPriceLabelProps } from "../../card/PriceLabel"
import { QuantityBadgeFragment } from "../../card/QuantityBadge"
import { RarityBadgeFragment } from "../../card/RarityBadge"
import { GridItemSkeleton } from "../../GridItemSkeleton"
import { ItemLink, ItemLinkFragment } from "../../ItemLink"
import { useItemView } from "../../ItemViewProvider"
import { useAcceptOffers } from "../../use-accept-offers"
import { useAcceptOffersFragment } from "../../use-accept-offers.fragment"
import { useBuyItems } from "../../use-buy-items"
import { useBuyItemsFragment } from "../../use-buy-items.fragment"
import { useCancelItemsListings } from "../../use-cancel-items-listings"
import { useCancelItemsListingsFragment } from "../../use-cancel-items-listings.fragment"
import { useCancelOrders } from "../../use-cancel-orders"
import { useCancelOrdersFragment } from "../../use-cancel-orders.fragment"
import { useListItems } from "../../use-list-items"
import { useListItemsFragment } from "../../use-list-items.fragment"
import {
  getIsItemOwnedBy,
  getItemOwnedQuantity,
  ItemOwnedQuantityFragment,
  itemUrl,
} from "../../utils"
import {
  isItemTradable,
  isItemTradableFragment,
} from "../../validation/tradable"
import { useCollectionItemsSelectionStore } from "../CollectionItemsSelectionProvider"
import { useCollectionItemsSelection } from "../use-collection-items-selection"

export const CollectionItemsCardFragment = graphql(
  `
    fragment CollectionItemsCardFragment on Item {
      id
      chain {
        identifier
      }
      contractAddress
      tokenId
      isFungible
      lastSale {
        ...TokenPrice
      }
      totalSupply
      bestListing {
        id
        pricePerItem {
          ...TokenPrice
        }
        marketplace {
          identifier
        }
        quantityRemaining
        maker {
          address
        }
        endTime
        ...useCancelOrders
      }
      enforcement {
        isDisabled
        isCompromised
      }
      ...bestItemOffer
      ...ItemOwnedQuantity
      ...useAcceptOffers
      ...useBuyItems
      ...useListItems
      ...EnforcementBadge
      ...ItemCardMedia
      ...ItemCardNameFragment
      ...QuantityBadge
      ...RarityBadgeFragment
      ...ItemLink
      ...useCancelItemsListings
      ...isItemTradable
    }
  `,
  [
    ItemCardMediaFragment,
    ItemCardNameFragment,
    QuantityBadgeFragment,
    RarityBadgeFragment,
    ItemLinkFragment,
    TokenPriceFragment,
    useBuyItemsFragment,
    useListItemsFragment,
    EnforcementBadgeFragment,
    useAcceptOffersFragment,
    useCancelItemsListingsFragment,
    useCancelOrdersFragment,
    bestItemOfferFragment,
    ItemOwnedQuantityFragment,
    isItemTradableFragment,
  ],
)

const SHOW_COUNTDOWN_MINUTES_CUTOFF = 15

type Props = {
  item: FragmentOf<typeof CollectionItemsCardFragment> | undefined
  index: number
  onClick?: () => void
  disableLink?: boolean
  disableActions?: boolean
}

export function CollectionItemsCard({
  item: itemKey,
  index,
  onClick,
  disableLink,
  disableActions,
}: Props) {
  const { action } = useCollectionItemsSelectionStore()
  const { statuses } = useStatusQueryParam()
  const showingOwnedByYou = statuses.includes("ownedByYou")
  const isPurchaseMode = action === "buy"
  const [frameTime, setFrameTime] = useState<number | undefined>(1)
  const item = readFragment(CollectionItemsCardFragment, itemKey)
  const { itemView } = useItemView()
  const ownedQuantity = getItemOwnedQuantity(item)
  const showRarity = useContextSelector(
    CollectionSettingsContext,
    state => state.showRarity,
  )

  const [
    allItems,
    selectedItems,
    selectItem,
    updateSelectionQuantity,
    isItemSelectable,
  ] = useCollectionItemsSelection(store => [
    store.allItems,
    store.selectedItems,
    store.selectItem,
    store.updateSelectionQuantity,
    store.isItemSelectable,
  ])

  const { handleOnClick } = useSelectItemMouseShortcuts({
    items: allItems,
    selectedItems,
    selectItem,
  })

  function renderBadge() {
    if (item?.isFungible) {
      if (showingOwnedByYou) {
        return (
          <QuantityBadge>
            <NumberDisplay display="quantity" value={ownedQuantity} />
          </QuantityBadge>
        )
      }
      return item.totalSupply ? (
        <QuantityBadge>
          <NumberDisplay display="quantity" value={item.totalSupply} />
        </QuantityBadge>
      ) : null
    }
    return showRarity ? <ItemCardRarityBadge item={item} /> : null
  }

  if (!item) {
    return <GridItemSkeleton />
  }

  const isSelected = selectedItems.has(item.id)
  const maxQuantity = isPurchaseMode
    ? Number(item.bestListing?.quantityRemaining ?? 0)
    : ownedQuantity.toNumber()
  const selectedQuantity = selectedItems.get(item.id)?.quantity ?? 0
  const inSelectionMode = selectedItems.size > 0

  return (
    <LiveAnimation itemKey={item.id} variant="grid">
      <HandoffButton>
        <Track click={{ event: "Item Card Click", data: { item } }}>
          <ItemCard active={isSelected}>
            <ItemLink
              className="flex h-full flex-col"
              disableLink={disableLink}
              item={item}
              onClick={e => {
                if (inSelectionMode) {
                  e.preventDefault()

                  if (isItemSelectable(item.id)) {
                    handleOnClick(e, index)
                  }
                } else {
                  onClick?.()
                }
              }}
              onPointerEnter={() => setFrameTime(undefined)}
              onPointerLeave={() => setFrameTime(1)}
            >
              <ItemCardContent>
                <ItemCardMedia
                  adjustAspectRatio
                  className="h-full"
                  frameTime={frameTime}
                  item={item}
                />
                {isItemSelectable(item.id) || isSelected ? (
                  item.isFungible ? (
                    <ItemCardSelectItemWithQuantityBadge
                      max={maxQuantity}
                      selectedQuantity={selectedQuantity}
                      updateQuantity={(quantity: number) => {
                        if (quantity === 1 && !isSelected) {
                          selectItem(item.id)
                        } else {
                          updateSelectionQuantity(item.id, quantity)
                        }
                      }}
                    />
                  ) : (
                    <ItemCardSelectItemBadge
                      active={isSelected}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        handleOnClick(event, index)
                      }}
                    />
                  )
                ) : null}
              </ItemCardContent>

              {itemView !== "mosaic" && (
                <ItemCardFooter className="min-h-24">
                  <ItemCardFooterSection>
                    <SpaceBetween className="w-full items-center gap-3">
                      <ItemLink
                        className="min-w-0 flex-1"
                        item={item}
                        onClick={e => {
                          e.stopPropagation()
                        }}
                      >
                        <HandoffButton>
                          <ItemCardName item={item} />
                        </HandoffButton>
                      </ItemLink>

                      {renderBadge()}
                    </SpaceBetween>
                  </ItemCardFooterSection>
                  <ItemCardFooterSection className="pb-3.5">
                    {item.enforcement.isCompromised ? (
                      <SpaceBetween className="mb-6" />
                    ) : (
                      <EnforcementBadge
                        entity={item}
                        renderWhenNotEnforced={
                          <CollectionItemsCardPriceLabel
                            item={item}
                            itemView={itemView}
                          />
                        }
                      />
                    )}
                  </ItemCardFooterSection>
                </ItemCardFooter>
              )}
            </ItemLink>

            {itemKey && !disableActions ? (
              <ItemCardActions floating={itemView === "mosaic"}>
                <CollectionItemsCardCTA
                  item={itemKey}
                  quantity={selectedQuantity || 1}
                />
              </ItemCardActions>
            ) : null}
          </ItemCard>
        </Track>
      </HandoffButton>
    </LiveAnimation>
  )
}

function CollectionItemsCardPriceLabel({
  item,
  itemView,
}: {
  item: ResultOf<typeof CollectionItemsCardFragment>
  itemView: ItemView
}) {
  const t = useTranslations("CollectionItemsCard")
  const showMarketplaceLogos = useContextSelector(
    CollectionSettingsContext,
    state => state.showMarketplaceLogos,
  )
  const priceLabelProps = {
    size: itemView === "gridCompact" ? "xs" : undefined,
  } as ItemCardPriceLabelProps

  if (item.bestListing) {
    const showCountdown = item.bestListing.endTime
      ? differenceInMinutes(new Date(item.bestListing.endTime), Date.now()) <=
        SHOW_COUNTDOWN_MINUTES_CUTOFF
      : false

    return (
      <FlexColumn>
        <ItemCardFooterDescription size="sm">
          <TokenPrice
            className="items-center gap-1.5"
            marketplace={
              showMarketplaceLogos
                ? item.bestListing.marketplace?.identifier
                : undefined
            }
            marketplaceIconSize={14}
            price={item.bestListing.pricePerItem}
          />
        </ItemCardFooterDescription>
        {showCountdown || item.lastSale ? (
          <ItemCardFooterDescription className="-mt-1 text-text-secondary">
            {showCountdown && item.bestListing.endTime ? (
              <TextBody className="text-[12px]">
                {t("endsIn")}{" "}
                <span className="font-mono">
                  <LiveTimestamp
                    date={new Date(item.bestListing.endTime)}
                    display="split"
                    units={{ hours: false }}
                  />
                </span>
              </TextBody>
            ) : null}
            {!showCountdown && item.lastSale ? (
              <TextOverflowTooltip>
                <TextBody className="text-[10px] xl:text-[12px]">
                  {t("lastSale")}{" "}
                  <TokenPrice
                    className="text-[9px] xl:text-[11px]"
                    price={item.lastSale}
                  />
                </TextBody>
              </TextOverflowTooltip>
            ) : null}
          </ItemCardFooterDescription>
        ) : null}
      </FlexColumn>
    )
  }

  if (item.lastSale) {
    return (
      <ItemCardPriceLabel
        component={<TokenPrice price={item.lastSale} />}
        label={t("lastSale")}
        {...priceLabelProps}
      />
    )
  }
  const bestOffer = getItemBestOffer(item)
  if (bestOffer) {
    return (
      <ItemCardPriceLabel
        component={<TokenPrice price={bestOffer.pricePerItem} />}
        label={t("topOffer")}
        {...priceLabelProps}
      />
    )
  }

  return <ItemCardPriceLabel label={t("notListed")} {...priceLabelProps} />
}

function CollectionItemsCardCTA({
  item: itemKey,
  quantity = 1,
}: {
  item: NonNullable<Props["item"]>
  quantity?: number
}) {
  const t = useTranslations("CollectionItemsCard")
  const item = readFragment(CollectionItemsCardFragment, itemKey)
  const { action } = useCollectionItemsSelectionStore()
  const { itemView } = useItemView()
  const isSellMode = action === "sell"
  const {
    buyItems,
    disabled: isBuyDisabled,
    disabledReason: buyDisabledReason,
  } = useBuyItems([{ ...item, quantity }])
  const { canCancelOrders } = useCancelOrders(
    item.bestListing ? [item.bestListing] : [],
  )

  const isListed = Boolean(item.bestListing)
  const { cancelItemsListings } = useCancelItemsListings([item])
  const { disabled: isAcceptOffersDisabled, acceptOffers } = useAcceptOffers([
    { ...item, quantity },
  ])
  const createOffer = useCreateOfferFlow()
  const {
    disabled: isListDisabled,
    disabledReason: listDisabledReason,
    listItems,
  } = useListItems([{ ...item, quantity }])
  const tokenPriceMarketplaceProps = useItemCardTokenPriceMarketplaceProps(
    item.bestListing?.marketplace?.identifier,
  )

  if (item.enforcement.isDisabled || item.enforcement.isCompromised) {
    const itemLink = readFragment(ItemLinkFragment, item)

    return (
      <Link className="w-full" href={itemUrl(itemLink)}>
        <ItemCardActionButton>
          <ItemCardActionSection>{t("viewItem")}</ItemCardActionSection>
        </ItemCardActionButton>
      </Link>
    )
  }

  const isOwner = getIsItemOwnedBy(item)
  const hasOffer = Boolean(getItemBestOffer(item))

  if (isOwner && canCancelOrders) {
    return (
      <Tooltip content={listDisabledReason} disabled={!isListDisabled}>
        <ItemCardTooltipButtonWrapper>
          <ItemCardActionButton
            disabled={isListDisabled}
            onClick={() => {
              cancelItemsListings()
            }}
          >
            <ItemCardActionSection>{t("cancelListing")}</ItemCardActionSection>
          </ItemCardActionButton>
        </ItemCardTooltipButtonWrapper>
      </Tooltip>
    )
  }

  if (isSellMode && isOwner) {
    return (
      <Tooltip content={listDisabledReason} disabled={!isListDisabled}>
        <ItemCardTooltipButtonWrapper>
          <ItemCardActionButton
            disabled={isListDisabled}
            onClick={() => {
              if (hasOffer && !isAcceptOffersDisabled) {
                acceptOffers()
              } else {
                listItems()
              }
            }}
          >
            <ItemCardActionSection>{t("sellNow")}</ItemCardActionSection>
          </ItemCardActionButton>
        </ItemCardTooltipButtonWrapper>
      </Tooltip>
    )
  }

  if (isListed) {
    return (
      <Tooltip content={buyDisabledReason} disabled={!isBuyDisabled}>
        <ItemCardTooltipButtonWrapper>
          <ItemCardActionButton
            disabled={isBuyDisabled}
            onClick={() => {
              buyItems()
            }}
          >
            <ItemCardActionSection
              className={itemCardActionVariants({ itemView }).section()}
            >
              <TextBody size="sm">
                {quantity > 1
                  ? t("buyMultiple", { quantity: quantity.toLocaleString() })
                  : t("buyNow")}
              </TextBody>
              <TokenPrice
                className={itemCardActionVariants({ itemView }).tokenPrice()}
                multiplier={quantity}
                price={item.bestListing?.pricePerItem}
                symbolColor="white"
                valueColor="white"
                {...tokenPriceMarketplaceProps}
              />
            </ItemCardActionSection>
          </ItemCardActionButton>
        </ItemCardTooltipButtonWrapper>
      </Tooltip>
    )
  }

  if (
    !isItemTradable(item) ||
    isAddressEqual(item.contractAddress, CRYPTO_PUNKS_ADDRESS) ||
    isOwner
  ) {
    return null
  }

  return (
    <ItemCardActionButton
      onClick={() => {
        createOffer([
          {
            chain: { identifier: item.chain.identifier },
            contractAddress: item.contractAddress as `0x${string}`,
            tokenId: item.tokenId,
            quantity,
          },
        ])
      }}
    >
      <ItemCardActionSection>{t("makeOffer")}</ItemCardActionSection>
    </ItemCardActionButton>
  )
}
